import React from 'react';
import { arrayOf, func, node, object, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { FieldHelp } from '../../components';

import css from './FieldSelect.module.css';

const handleChange = (propsOnChange, inputOnChange) => event => {
  // If "onChange" callback is passed through the props,
  // it can notify the parent when the content of the input has changed.
  if (propsOnChange) {
    // "handleChange" function is attached to the low level <select> component
    // value of the element needs to be picked from target
    const value = event.nativeEvent.target.value;
    propsOnChange(value);
  }
  // Notify Final Form that the input has changed.
  // (Final Form knows how to deal with synthetic events of React.)
  inputOnChange(event);
};

/**
 * A native select from the Components board: label, the select (the device's own list opens), and
 * one line under it (`helpText`, or the error instead). Pass `options` ([{ key, label }]) with an
 * optional `placeholder`, or <option> children as in the template.
 */
const FieldSelectComponent = props => {
  const {
    rootClassName,
    className,
    selectClassName,
    id,
    label,
    helpText,
    options,
    placeholder,
    input,
    meta,
    children,
    onChange,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { invalid, touched, error } = meta;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!(touched && invalid && error);
  const helpId = id ? `${id}-help` : null;

  const selectClasses = classNames(selectClassName, css.select, {
    [css.placeholder]: !input.value,
    [css.selectError]: hasError,
  });
  const { onChange: inputOnChange, ...restOfInput } = input;
  const selectProps = {
    className: selectClasses,
    id,
    onChange: handleChange(onChange, inputOnChange),
    'aria-invalid': hasError ? 'true' : undefined,
    'aria-describedby': (hasError || helpText) && helpId ? helpId : undefined,
    ...restOfInput,
    ...rest,
  };

  const optionElements = options
    ? [
        placeholder ? (
          <option key="placeholder" disabled value="">
            {placeholder}
          </option>
        ) : null,
        ...options.map(o => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        )),
      ]
    : children;

  const classes = classNames(rootClassName || css.root, className);
  return (
    <div className={classes}>
      {label ? <label htmlFor={id}>{label}</label> : null}
      <select {...selectProps}>{optionElements}</select>
      <FieldHelp id={helpId} helpText={helpText} errorText={hasError ? error : null} />
    </div>
  );
};

FieldSelectComponent.defaultProps = {
  helpText: null,
  options: null,
  placeholder: null,
  rootClassName: null,
  className: null,
  selectClassName: null,
  id: null,
  label: null,
  children: null,
};

FieldSelectComponent.propTypes = {
  rootClassName: string,
  className: string,
  selectClassName: string,

  onChange: func,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,
  // The line under the field
  helpText: node,
  // [{ key, label }], instead of <option> children
  options: arrayOf(shape({ key: string.isRequired, label: node.isRequired })),
  // A first, unselectable option shown while nothing is chosen
  placeholder: string,

  // Generated by final-form's Field component
  input: object.isRequired,
  meta: object.isRequired,

  children: node,
};

const FieldSelect = props => {
  return <Field component={FieldSelectComponent} {...props} />;
};

export default FieldSelect;
